import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, Button, Alert, Box, FormControl, InputLabel, Select, MenuItem, CircularProgress, SelectChangeEvent
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { format } from 'date-fns';
import { taskService } from '../services/taskService';
import { Project } from '../types/project';
import { NewTask } from '../types/task';
import { User } from '../types/user';
import { projectService } from '../services/projectService';
import { userService } from '../services/userService';

interface AddTaskModalProps {
  open: boolean;
  onClose: () => void;
  onSave: () => void;
}

const AddTaskModal: React.FC<AddTaskModalProps> = ({ open, onClose, onSave }) => {
  const [newTask, setNewTask] = useState<Partial<NewTask>>({ status: 'TODO' });
  const [projects, setProjects] = useState<Project[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (open) {
      const fetchDropdownData = async () => {
        setLoading(true);
        try {
          const [projectData, userData] = await Promise.all([
            projectService.getProjects(),
            userService.getUsers(),
          ]);
          setProjects(projectData);
          setUsers(userData);
        } catch (err) {
          setError('Could not load required data.');
        } finally {
          setLoading(false);
        }
      };
      fetchDropdownData();
    }
  }, [open]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent<any>) => {
    const { name, value } = e.target;
    setNewTask((prev: Partial<NewTask>) => ({
      ...prev,
      [name as string]: value
    }));
  };

  const handleDateChange = (date: Date | null) => {
    if (date) {
        setNewTask((prev: Partial<NewTask>) => ({ ...prev, due_date: format(date, 'yyyy-MM-dd') }));
    } else {
        setNewTask((prev: Partial<NewTask>) => ({ ...prev, due_date: null }));
    }
  };

  const handleSubmit = async () => {
    if (!newTask.title || !newTask.project || !newTask.assigned_to) {
      setError('Task title, project, and assigned user are required.');
      return;
    }
    setIsSaving(true);
    setError(null);
    try {
      await taskService.createTask(newTask as NewTask);
      onSave();
      onClose();
      setNewTask({ status: 'TODO' }); // Reset form
    } catch (err: any) {
      console.error('Failed to create task:', err);
      setError(err.response?.data?.detail || 'An unexpected error occurred.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Add New Task</DialogTitle>
      <DialogContent>
        <Box component="form" noValidate sx={{ mt: 1 }}>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          {loading ? <CircularProgress /> : (
            <>
              <TextField margin="normal" required fullWidth label="Task Title" name="title" onChange={handleChange} autoFocus />
              <FormControl fullWidth margin="normal" required>
                <InputLabel id="project-select-label">Project</InputLabel>
                <Select labelId="project-select-label" name="project" value={newTask.project || ''} label="Project" onChange={handleChange}>
                  {projects.map((project) => (
                    <MenuItem key={project.id} value={project.id}>{project.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth margin="normal" required>
                <InputLabel id="user-select-label">Assign To</InputLabel>
                <Select labelId="user-select-label" name="assigned_to" value={newTask.assigned_to || ''} label="Assign To" onChange={handleChange}>
                  {users.map((user) => (
                    <MenuItem key={user.id} value={user.id}>{user.first_name} {user.last_name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField margin="normal" fullWidth label="Description" name="description" multiline rows={3} onChange={handleChange} />
              <DatePicker
                label="Due Date"
                value={newTask.due_date ? new Date(newTask.due_date) : null}
                onChange={handleDateChange}
                sx={{ width: '100%', mt: 2, mb: 1 }}
              />
              <FormControl fullWidth margin="normal">
                <InputLabel id="status-select-label">Status</InputLabel>
                <Select labelId="status-select-label" name="status" value={newTask.status || 'TODO'} label="Status" onChange={handleChange}>
                  <MenuItem value="TODO">To Do</MenuItem>
                  <MenuItem value="IN_PROGRESS">In Progress</MenuItem>
                  <MenuItem value="DONE">Done</MenuItem>
                </Select>
              </FormControl>
            </>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSubmit} variant="contained" disabled={isSaving || loading}>
          {isSaving ? 'Saving...' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddTaskModal;